// Helpers for parsing query-param maps + the shared contract.
import { parseQueryParams, ReusePolicy } from '../helpers';

/**
 * SMART — "cached params must still be present and unchanged in the target".
 *
 * Middle ground between preserveAlways (never evict) and strict (evict on any
 * URL diff). Use this if you want a route to evict when its identifying
 * query params actually change, but tolerate (a) Angular reordering params
 * and (b) sub-routes appending extra context params that get carried back
 * on return navigation.
 *
 * Rule:
 *   For every key in the CACHED URL's query string, the target URL must
 *   contain the same key with the same value. Extra keys in the target
 *   are ignored. Param ordering is ignored too — we compare value maps,
 *   not strings.
 *
 * Examples (assume /foo cached with ?cboUuid=abc&blockId=1):
 *
 *   target:  /foo?blockId=1&cboUuid=abc                          → KEEP (reorder)
 *   target:  /foo?cboUuid=abc&blockId=1&fundType=257             → KEEP (extras OK)
 *   target:  /foo?cboUuid=xyz&blockId=1                          → EVICT (value changed)
 *   target:  /foo?cboUuid=abc                                    → EVICT (lost a key)
 *
 * Opt in with `data: { reuseRoute: 'smart' }`. No route uses this today —
 * cut-off-meeting was the prime candidate but works fine on the default
 * preserveAlways policy because sidebar clicks already invalidate the cache.
 */
export const smartPolicy: ReusePolicy = {
    // The string `data.reuseRoute: 'smart'` resolves to this policy.
    name: 'smart',

    shouldEvict(cached, targetUrl) {
        const cachedParams = parseQueryParams(cached.fullUrl);
        const targetParams = parseQueryParams(targetUrl);
        // Walk the cached entity's identifying params. If even one is missing
        // from target OR has a different value there, the user is on a
        // different entity (or lost identifying state), so evict.
        return Object.keys(cachedParams).some(
            (k) => cachedParams[k] !== targetParams[k],
        );
    },
};
